import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_ENDPOINTS } from '../config/api'; 

const About = () => {
  const [team, setTeam] = useState([]);
  const [content, setContent] = useState('');
  const [heroImages, setHeroImages] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    fetchTeam();
    fetchPageContent();
    fetchHeroImages();
    
    // Auto-slide hero images
    const heroInterval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % (heroImages.length || 1));
    }, 5000);

    return () => {
      clearInterval(heroInterval);
    };
  }, [heroImages.length]);

  const fetchTeam = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.TEAM);
      if (response.data.success) {
        setTeam(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching team:', error);
      // Optional: Set mock data for development
      setTeam([
        {
          id: 1,
          name: 'John Doe',
          position: 'Executive Director',
          bio: 'Leading our organization with 15+ years of experience...',
          email: 'john@sikom.org'
        },
        {
          id: 2,
          name: 'Jane Smith',
          position: 'Program Manager',
          bio: 'Overseas all community development programs...',
          email: 'jane@sikom.org'
        }
      ]);
    }
  };

  const fetchPageContent = async () => {
    try {
      // If your API expects a specific format for pages, adjust accordingly
      const response = await axios.get(API_ENDPOINTS.PAGES, {
        params: { page: 'about' } // Send page parameter if needed
      });
      
      // Adjust based on your actual API response structure
      if (response.data.success) {
        setContent(response.data.data.content);
      } else {
        // Fallback content
        setContent(`
          <h3>About SIKOM</h3>
          <p>SIKOM is a non-governmental organization established in 2001 as a self-help group and registered in 2006 as a national NGO in Kenya. The name "SIKOM" comes from the Pokot word meaning "coming together for a positive cause."</p>
          <p>Our organization focuses on peacebuilding, sustainable development, and community empowerment in the North Rift region of Kenya and the Lower Karamoja cluster.</p>
          <h4>Our Mission</h4>
          <p>To foster peace and sustainable development among communities through innovative programs and partnerships.</p>
          <h4>Our Vision</h4>
          <p>Healthy, peaceful, equitable, inclusive, and resilient communities.</p>
        `);
      }
    } catch (error) {
      console.error('Error fetching page content:', error);
      // Fallback content
      setContent(`
        <h3>About SIKOM</h3>
        <p>SIKOM is a non-governmental organization established in 2001 as a self-help group and registered in 2006 as a national NGO in Kenya. The name "SIKOM" comes from the Pokot word meaning "coming together for a positive cause."</p>
        <p>Our organization focuses on peacebuilding, sustainable development, and community empowerment in the North Rift region of Kenya and the Lower Karamoja cluster.</p>
      `);
    }
  };

  const fetchHeroImages = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.HERO_IMAGES, {
        params: { is_active: 1 }
      });
      if (response.data.success) {
        setHeroImages(response.data.data.map(img => img.image_path));
      } else {
        setHeroImages([
          '/images/hero1.jpg',
          '/images/hero2.jpg',
          '/images/hero3.jpg'
        ]);
      }
    } catch (error) {
      console.error('Error fetching hero images:', error);
      setHeroImages([
        '/images/hero1.jpg',
        '/images/hero2.jpg',
        '/images/hero3.jpg'
      ]);
    }
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  // Rest of your component remains the same...
  return (
    <div className="about-page">
      <style>{`
        .hero-section {
          min-height: 60vh;
          position: relative;
          overflow: hidden;
        }

        .hero-slider {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          z-index: 0;
        }

        .hero-slide {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-size: cover;
          background-position: center;
          transition: opacity 1.5s ease-in-out, transform 8s ease-out;
        }

        .hero-slide.active {
          animation: kenBurns 8s ease-out;
        }

        @keyframes kenBurns {
          0% { transform: scale(1); }
          100% { transform: scale(1.1); }
        }

        .slider-dot {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          border: 2px solid white;
          background: transparent;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .slider-dot.active {
          background: white;
          transform: scale(1.2);
        }
      `}</style>
      
      {/* Hero */}
      <section className="hero-section position-relative text-white">
        {/* Background Image Slider */}
        <div className="hero-slider">
          {heroImages.map((image, index) => (
            <div
              key={index}
              className={`hero-slide ${index === currentSlide ? 'active' : ''}`}
              style={{
                backgroundImage: `url(${image})`,
                opacity: index === currentSlide ? 1 : 0,
                transform: index === currentSlide ? 'scale(1)' : 'scale(1.1)',
              }}
            />
          ))}
          
          {/* Overlay */}
          <div 
            className="position-absolute top-0 start-0 w-100 h-100"
            style={{
              background: 'linear-gradient(135deg, rgba(13, 110, 253, 0.85) 0%, rgba(108, 117, 125, 0.75) 100%)'
            }}
          />
        </div>

        {/* Content */}
        <div className="container position-relative" style={{ zIndex: 2, paddingTop: '100px', paddingBottom: '100px' }}>
          <div className="row align-items-center">
            <div className="col-lg-8">
              <h1 className="display-4 fw-bold mb-3">About SIKOM</h1>
              <p className="lead mb-0">Peace Network for Development</p>
            </div>
          </div>
        </div>

        {/* Slider Indicators */}
        <div 
          className="position-absolute bottom-0 start-50 translate-middle-x mb-4"
          style={{ zIndex: 3 }}
        >
          <div className="d-flex gap-2">
            {heroImages.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`slider-dot ${index === currentSlide ? 'active' : ''}`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Who We Are */}
      <section className="py-5">
        <div className="container">
          <div className="row">
            <div className="col-lg-8">
              <div dangerouslySetInnerHTML={{ __html: content }} />
              
              <div className="mt-5">
                <h3 className="mb-4">Our Goal</h3>
                <div className="card bg-light border-0">
                  <div className="card-body">
                    <p className="lead mb-0">
                      Healthy, peaceful, equitable, inclusive and resilient communities
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="col-lg-4">
              <div className="card border-0 shadow-sm">
                <div className="card-body">
                  <h5 className="card-title">Quick Facts</h5>
                  <ul className="list-unstyled">
                    <li className="mb-3">
                      <strong>Established:</strong> 2001 as self-help group
                    </li>
                    <li className="mb-3">
                      <strong>Registered:</strong> 2006 as national NGO
                    </li>
                    <li className="mb-3">
                      <strong>Meaning of SIKOM:</strong> Pokot word for "coming together for a positive course"
                    </li>
                    <li className="mb-3">
                      <strong>Headquarters:</strong> Kapenguria, West Pokot County
                    </li>
                    <li>
                      <strong>Coverage:</strong> North Rift Kenya and Lower Karamoja Cluster
                    </li>
                  </ul>
                </div>
              </div>
              
              <div className="card border-0 shadow-sm mt-4">
                <div className="card-body">
                  <h5 className="card-title">Strategy of Operation</h5>
                  <ul className="list-unstyled">
                    <li className="mb-2"><i className="bi bi-search text-primary me-2"></i> Research</li>
                    <li className="mb-2"><i className="bi bi-clipboard-check text-primary me-2"></i> Effective Planning</li>
                    <li className="mb-2"><i className="bi bi-people text-primary me-2"></i> Capacity Building</li>
                    <li><i className="bi bi-megaphone text-primary me-2"></i> Advocacy</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Meet Our Team */}
      <section className="py-5 bg-light">
        <div className="container">
          <div className="row mb-5">
            <div className="col-12 text-center">
              <h2 className="display-5 fw-bold">Meet Our Team</h2>
              <p className="lead">Dedicated professionals driving our mission</p>
            </div>
          </div>
          
          <div className="row g-4">
            {team.map((member) => (
              <div className="col-md-4 col-lg-3" key={member.id}>
                <div className="card border-0 shadow-sm text-center">
                  <div className="card-body">
                    <div className="mb-3">
                      {member.image ? (
                        <img 
                          src={member.image} 
                          alt={member.name}
                          className="rounded-circle"
                          width="120"
                          height="120"
                          style={{objectFit: 'cover'}}
                        />
                      ) : (
                        <div className="rounded-circle bg-primary d-inline-flex align-items-center justify-content-center"
                             style={{width: '120px', height: '120px'}}>
                          <span className="text-white display-5">
                            {member.name.charAt(0)}
                          </span>
                        </div>
                      )}
                    </div>
                    <h5 className="card-title">{member.name}</h5>
                    <p className="text-muted">{member.position}</p>
                    <p className="card-text small">{member.bio?.substring(0, 100)}...</p>
                    <div className="mt-3">
                      {member.email && (
                        <a href={`mailto:${member.email}`} className="btn btn-sm btn-outline-primary me-2">
                          <i className="bi bi-envelope"></i>
                        </a>
                      )}
                      {member.phone && (
                        <a href={`tel:${member.phone}`} className="btn btn-sm btn-outline-primary">
                          <i className="bi bi-telephone"></i>
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partner With Us */}
      <section className="py-5">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-8">
              <h2 className="display-5 fw-bold mb-4">Partner With Us</h2>
              <p className="lead mb-4">
                SIKOM is dedicated to fostering peace and sustainable development among 
                communities through innovative programs and partnerships.
              </p>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <div className="d-flex align-items-center">
                    <i className="bi bi-handshake display-6 text-primary me-3"></i>
                    <div>
                      <h5>Collaborate</h5>
                      <p className="mb-0">Work with donors, partners, and government departments</p>
                    </div>
                  </div>
                </div>
                <div className="col-md-6 mb-3">
                  <div className="d-flex align-items-center">
                    <i className="bi bi-lightbulb display-6 text-primary me-3"></i>
                    <div>
                      <h5>Innovate</h5>
                      <p className="mb-0">Leverage multisectoral stakeholders' partnerships</p>
                    </div>
                  </div>
                </div>
              </div>
              <p className="mb-4">
                Whether through funding or knowledge exchange, we welcome diverse 
                collaborative efforts to contribute to our mission.
              </p>
              <div className="d-flex gap-3">
                <a href="mailto:sikompeace@yahoo.com" className="btn btn-primary">
                  <i className="bi bi-envelope me-2"></i> Email Us
                </a>
                <a href="/contact" className="btn btn-outline-primary">
                  Contact Form
                </a>
              </div>
            </div>
            
            <div className="col-lg-4">
              <div className="card border-0 shadow-sm bg-primary text-white">
                <div className="card-body p-4">
                  <h5 className="card-title">Join Our Mission</h5>
                  <p className="card-text">
                    Connect with us today to explore partnership opportunities
                  </p>
                  <ul className="list-unstyled">
                    <li className="mb-2">
                      <i className="bi bi-envelope me-2"></i>
                      sikompeace@yahoo.com
                    </li>
                    <li className="mb-2">
                      <i className="bi bi-envelope me-2"></i>
                      info@sikompeace.org
                    </li>
                    <li>
                      <i className="bi bi-phone me-2"></i>
                      +254711538258
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;